import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  startAssignSpecialistCases,
  startPreviewReassignChain
} from "../../app/actions/specialistSelectionActions";
import {
  formatSpecialistUserLabel,
  summarizeCaseMassSelection
} from "../../app/helpers/flowCaseMassHelper";
import { getModuleDisplayLabel } from "../../app/helpers/flowEngineConfigHelper";
import "./flowMassReassignPanel.css";

const POOL_VALUE = "__pool__";

const FlowMassReassignPanel = ({
  records = [],
  users = [],
  roleKey,
  activeRoleKey = "",
  canReassign = false,
  loading = false,
  currentUserId,
  onAssigned
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const summary = useMemo(() => summarizeCaseMassSelection(records), [records]);
  const [query, setQuery] = useState("");
  const [targetUserId, setTargetUserId] = useState("");
  const [originManagerUserId, setOriginManagerUserId] = useState("");
  const [originOptions, setOriginOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const inboxSurface = useMemo(() => {
    const lower = String(roleKey || "").toLowerCase();
    if (lower.includes("validator") || lower.includes("verificator") || lower.includes("verificador")) return "validator";
    if (lower.includes("editor")) return "editor";
    if (lower.includes("pre-manager") || lower.includes("pre-gestor")) return "pre-manager";
    if (lower.includes("coordinator") || lower.includes("coordinador")) return "coordinator";
    if (lower.includes("liaison") || lower.includes("enlace")) return "liaison";
    if (lower.includes("manager")) return "manager";
    return "";
  }, [roleKey]);

  const toPool = targetUserId === POOL_VALUE;

  const filteredUsers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return users;
    }
    return users.filter((user) => formatSpecialistUserLabel(user).toLowerCase().includes(needle));
  }, [query, users]);

  useEffect(() => {
    setTargetUserId("");
    setOriginManagerUserId("");
    setOriginOptions([]);
    setQuery("");
  }, [summary.process, summary.roleId, summary.ciiuId, records.length]);

  useEffect(() => {
    const recordIds = records.map((row) => row.id);
    if (
      !canReassign
      || !summary.canReassign
      || !targetUserId
      || toPool
      || !recordIds.length
      || inboxSurface === "manager"
    ) {
      setOriginOptions([]);
      return undefined;
    }
    let cancelled = false;
    dispatch(
      startPreviewReassignChain({
        roleKey,
        moduleName: summary.process,
        recordIds,
        targetUserId,
        activeRoleKey
      })
    ).then((result) => {
      if (cancelled) {
        return;
      }
      const options = result?.origin_options || [];
      setOriginOptions(options);
      const suggested = result?.suggested_origin_manager_user_id;
      if (suggested && options.some((row) => Number(row.id) === Number(suggested))) {
        setOriginManagerUserId(String(suggested));
      } else if (options.length === 1) {
        setOriginManagerUserId(String(options[0].id));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [activeRoleKey, canReassign, dispatch, inboxSurface, records, roleKey, summary.canReassign, summary.process, targetUserId, toPool]);

  if (!canReassign) {
    return null;
  }

  if (!records.length) {
    return (
      <div className="alert alert-light border py-2 small mb-3">
        {t(
          "flow.mass.reassign.select-hint",
          "Selecciona uno o más casos para reasignarlos a un usuario del rol de esta bandeja o a la bandeja de autoasignación."
        )}
      </div>
    );
  }

  const blockReason = (() => {
    if (summary.anyPublished) {
      return t("flow.mass.reassign.published", "Los registros publicados no se reasignan desde esta bandeja.");
    }
    if (!summary.sameModule) {
      return t("flow.mass.reassign.mixed-module", "Selecciona registros del mismo módulo para reasignar.");
    }
    if (!summary.sameState) {
      return t("flow.mass.reassign.mixed-state", "Selecciona registros del mismo estado para reasignar.");
    }
    if (!summary.sameTray) {
      return t("flow.mass.reassign.mixed-tray", "Selecciona registros de la misma bandeja para reasignar.");
    }
    if (!summary.sameCiiu || !summary.ciiuId) {
      return t("flow.mass.reassign.mixed-ciiu", "Selecciona registros del mismo CIIU para reasignar.");
    }
    return "";
  })();

  const handleSubmit = async () => {
    if (!summary.canReassign) {
      Swal.fire(t("titles.info"), blockReason, "info");
      return;
    }
    if (!targetUserId) {
      Swal.fire(
        t("titles.info"),
        t("specialist.selection.validation.targetUser", "Selecciona el usuario destino o la bandeja"),
        "info"
      );
      return;
    }
    setSubmitting(true);
    const success = await dispatch(
      startAssignSpecialistCases({
        roleKey,
        moduleName: summary.process,
        recordIds: records.map((row) => row.id),
        targetUserId: toPool ? undefined : targetUserId,
        targetMode: toPool ? "pool" : "user",
        originManagerUserId: toPool ? undefined : (originManagerUserId || undefined),
        activeRoleKey
      })
    );
    setSubmitting(false);
    if (success) {
      setTargetUserId("");
      setOriginManagerUserId("");
      onAssigned?.();
    }
  };

  return (
    <div className="flow-mass-reassign-panel">
      <div className="flow-mass-reassign-panel__body">
        <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
          <div>
            <strong>{t("flow.mass.reassign.title", "Reasignar seleccionados")}</strong>
            <div className="small text-muted">
              {t("flow.mass.reassign.count", "{{count}} registros", { count: records.length })}
            </div>
          </div>
          {summary.canReassign && (
            <div className="small text-muted">
              {t("flow.mass.reassign.scope", "Módulo {{module}} · CIIU {{ciiu}}", {
                module: getModuleDisplayLabel(summary.process, t),
                ciiu: summary.ciiuId
              })}
            </div>
          )}
        </div>

        {blockReason ? (
          <div className="alert alert-warning mb-0">{blockReason}</div>
        ) : (
          <div className="row g-2 align-items-end">
            <div className="col-lg-3">
              <label className="form-label">{t("specialist.selection.targetUser.search", "Busca por usuario, nombre, apellido o correo")}</label>
              <input
                type="search"
                className="form-control"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                disabled={toPool}
              />
            </div>
            <div className="col-lg-4">
              <label className="form-label">{t("specialist.selection.targetUser", "Destino")}</label>
              <select
                className="form-select"
                value={targetUserId}
                onChange={(event) => setTargetUserId(event.target.value)}
              >
                <option value="">{t("flow.cmi.choose-user", "Selecciona un destino")}</option>
                <option value={POOL_VALUE}>
                  {t("flow.mass.reassign.pool", "Bandeja (autoasignación / pool)")}
                </option>
                {filteredUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {formatSpecialistUserLabel(user)}
                    {Number(user.id) === Number(currentUserId)
                      ? ` — ${t("flow.cmi.me", "yo")}`
                      : ""}
                  </option>
                ))}
              </select>
            </div>
            {!toPool && (inboxSurface === "validator" || inboxSurface === "editor") && (
              <div className="col-lg-3">
                <label className="form-label">
                  {t("specialist.selection.originManager", "Gestor de origen")}
                </label>
                <select
                  className="form-select"
                  value={originManagerUserId}
                  onChange={(event) => setOriginManagerUserId(event.target.value)}
                  disabled={!targetUserId}
                >
                  <option value="">
                    {t("specialist.selection.originManager.auto", "Automático (cadena que cierra)")}
                  </option>
                  {originOptions.map((origin) => (
                    <option key={origin.id} value={origin.id}>
                      {formatSpecialistUserLabel(origin)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-lg-2 d-grid">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={submitting || loading || !targetUserId}
              >
                <i className="fa fa-user-plus me-1" />
                {t("specialist.selection.actions.reassign", "Reasignar")}
              </button>
            </div>
            <div className="col-12">
              <p className="small text-muted mb-0 flow-mass-reassign-panel__hint">
                {toPool
                  ? t(
                    "flow.mass.reassign.hint-pool",
                    "Quita el usuario asignado. El caso queda en la bandeja de autoasignación del rol actual para que otro lo tome."
                  )
                  : t(
                    "flow.mass.reassign.hint",
                    "Solo se reasigna si todos los seleccionados comparten módulo, estado y CIIU. El destino debe tener el rol de esa bandeja."
                  )}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

FlowMassReassignPanel.propTypes = {
  records: PropTypes.array,
  users: PropTypes.array,
  roleKey: PropTypes.string,
  activeRoleKey: PropTypes.string,
  canReassign: PropTypes.bool,
  loading: PropTypes.bool,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAssigned: PropTypes.func
};

export default FlowMassReassignPanel;
